import React from 'react';

interface SkeletonLoaderProps {
  variant?: 'text' | 'card' | 'table' | 'list' | 'dashboard';
  count?: number;
}

const Shimmer: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div
    className={`bg-gray-200 rounded animate-pulse ${className}`}
  />
);

const TextSkeleton: React.FC = () => (
  <div className="space-y-3">
    <Shimmer className="h-4 w-3/4" />
    <Shimmer className="h-4 w-full" />
    <Shimmer className="h-4 w-5/6" />
    <Shimmer className="h-4 w-2/3" />
  </div>
);

const CardSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
    <div className="flex items-center gap-3">
      <Shimmer className="w-10 h-10 rounded-lg" />
      <div className="flex-1 space-y-2">
        <Shimmer className="h-4 w-1/2" />
        <Shimmer className="h-3 w-1/3" />
      </div>
    </div>
    <Shimmer className="h-3 w-full" />
    <Shimmer className="h-3 w-4/5" />
    <div className="flex gap-2 pt-2">
      <Shimmer className="h-8 w-20 rounded-md" />
      <Shimmer className="h-8 w-20 rounded-md" />
    </div>
  </div>
);

const TableSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
    <div className="bg-gray-50 px-4 py-3 flex gap-4">
      {[1, 2, 3, 4].map((i) => (
        <Shimmer key={i} className="h-4 flex-1" />
      ))}
    </div>
    {[1, 2, 3, 4, 5].map((row) => (
      <div key={row} className="px-4 py-3 flex gap-4 border-t border-gray-100">
        {[1, 2, 3, 4].map((col) => (
          <Shimmer key={col} className="h-4 flex-1" />
        ))}
      </div>
    ))}
  </div>
);

const ListSkeleton: React.FC = () => (
  <div className="space-y-3">
    {[1, 2, 3].map((i) => (
      <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-4">
        <Shimmer className="w-12 h-12 rounded-lg flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <Shimmer className="h-4 w-2/3" />
          <Shimmer className="h-3 w-1/2" />
        </div>
        <Shimmer className="h-8 w-16 rounded-md" />
      </div>
    ))}
  </div>
);

const DashboardSkeleton: React.FC = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <Shimmer className="w-10 h-10 rounded-lg" />
            <Shimmer className="h-6 w-12 rounded" />
          </div>
          <Shimmer className="h-4 w-1/2 mb-1" />
          <Shimmer className="h-3 w-1/3" />
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <TableSkeleton />
      <ListSkeleton />
    </div>
  </div>
);

const variantMap = {
  text: TextSkeleton,
  card: CardSkeleton,
  table: TableSkeleton,
  list: ListSkeleton,
  dashboard: DashboardSkeleton,
};

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  variant = 'card',
  count = 1,
}) => {
  const SkeletonComponent = variantMap[variant];

  if (variant === 'dashboard' || variant === 'table') {
    return <SkeletonComponent />;
  }

  return (
    <div className="space-y-4">
      {Array.from({ length: count }, (_, i) => (
        <SkeletonComponent key={i} />
      ))}
    </div>
  );
};

export default SkeletonLoader;
